/**
 * Composable para gerenciar propostas de plano
 * Centraliza a lógica duplicada entre PlanSelectionV2 e Create
 */
import Vue from 'vue'
import priceCalculator from '@/shared/services/usePriceCalculator'
import moduleRelations from '@/shared/services/useModuleRelations'
import {
    showParentComboOptions,
    hideParentComboOptions,
} from '@/shared/services/useComboModules'
import {
    createInitialPlanProposal,
    addOrReplaceModuleInProposal,
    recalculateProposalPrices,
} from '@/shared/services/planProposalService'
import {
    findModuleIndex,
    hasCombo,
    hasDependency,
    hasCourtesy,
    filterFixedModules,
    filterOptionalModules,
    filterSelectedModules,
    createProposalModule,
    removeByIndices,
    findMatchingFixedModule,
} from '@/shared/utils/moduleUtils'

/**
 * Hook principal para gerenciar proposta de plano
 * @param {Object} options - Opções de configuração
 * @param {Object} options.vue - Instância do Vue (this) para acessar $refs, $set, etc
 * @param {Object} options.discountCode - Código de desconto (reativo)
 * @returns {Object} - Métodos e computed properties para gerenciar a proposta
 */
export function usePlanProposalManager(options = {}) {
    const { vue, discountCode } = options

    const state = Vue.observable({
        planProposal: createInitialPlanProposal(),
        comboItems: [],
        hasOptionalInvoice: false,
        displayFixedModules: [],
    })

    /**
     * Retorna todos os módulos fixos do pacote (sem filtro de invoice).
     * Uso interno: initializeFixedModules, removeCourtesy.
     */
    const getFixedModules = () => {
        if (!state.planProposal || !state.planProposal.planPackage) return []
        if (!state.planProposal.planPackage.modules) return []
        return filterFixedModules(state.planProposal.planPackage.modules)
    }

    const findInProposal = module => {
        const moduleId = module.module_id || (module.module && module.module.id)
        if (moduleId != null) {
            const byId = findModuleIndex(
                state.planProposal.proposal.modules,
                moduleId,
            )
            if (byId !== -1) return byId
        }

        return findModuleIndex(state.planProposal.proposal.modules, module)
    }

    const restoreFixedOrRemoveFromProposal = (module, existingModuleIndex) => {
        const fixedModule = findMatchingFixedModule(getFixedModules(), module)
        if (fixedModule) {
            addOrReplaceModuleInProposal(state.planProposal, {
                ...fixedModule,
                checked: false,
                add_amount: 0,
            })
            return
        }

        if (existingModuleIndex !== -1) {
            state.planProposal.proposal.modules.splice(existingModuleIndex, 1)
        }
    }

    /**
     * Recalcula state.displayFixedModules aplicando o filtro de invoice.
     * Deve ser chamada após qualquer mudança que afete a exibição dos fixos.
     */
    function refreshDisplayFixedModules() {
        const allFixed = getFixedModules()
        state.displayFixedModules = state.hasOptionalInvoice
            ? allFixed.filter(m => m.module && m.module.type !== 'invoices')
            : allFixed
    }

    /**
     * Módulos opcionais (do pacote + combos)
     */
    const getOptionalModules = () => {
        if (!state.planProposal || !state.planProposal.planPackage) return []
        if (!state.planProposal.planPackage.modules) return []
        return filterOptionalModules(
            state.planProposal.planPackage.modules,
            state.comboItems,
        )
    }

    /**
     * Módulos add-on selecionados
     */
    const getAddOnSelected = () => {
        if (!state.planProposal.proposal) return []
        return filterSelectedModules(state.planProposal.proposal.modules)
    }

    /**
     * Verifica se deve mostrar condição de fidelidade
     */
    const getShowLoyaltyCondition = () => {
        return (
            state.planProposal.planPackage &&
            (state.planProposal.planPackage.default_anual_discount > 0 ||
                (discountCode &&
                    discountCode.value &&
                    discountCode.value.discount_price > 0))
        )
    }

    /**
     * Verifica se usuário pode escolher fidelidade
     */
    const getAskLoyalty = () => {
        return (
            state.planProposal.planPackage &&
            state.planProposal.planPackage.default_anual_discount > 0 &&
            (!discountCode ||
                !discountCode.value ||
                discountCode.value.discount_price <= 0 ||
                discountCode.value.discount_price == null)
        )
    }

    /**
     * Key única para forçar re-render do summary quando módulos mudam
     */
    const getSummaryKey = () => {
        if (!state.planProposal || !state.planProposal.proposal) {
            return 'summary-empty'
        }

        const selectedSignature = state.planProposal.proposal.modules
            .map(
                m => `${m.module_id}:${m.checked ? 1 : 0}:${m.add_amount || 0}`,
            )
            .join('|')

        return `summary-${state.planProposal.total_price}-${state.planProposal.final_price}-${state.planProposal.proposal.discount}-${state.planProposal.proposal.loyalty}-${selectedSignature}-${state.hasOptionalInvoice}`
    }

    // ==================== MÉTODOS DE GERENCIAMENTO DE MÓDULOS ====================

    /**
     * Adiciona um módulo à proposta
     */
    function addModule(module) {
        state.planProposal.proposal.modules.push(createProposalModule(module))
    }

    function replaceInvoiceModule(newModule) {
        if (!state.planProposal.planPackage) return

        const packageModules = state.planProposal.planPackage.modules
        const indicesToRemove = []

        state.planProposal.proposal.modules.forEach((m, index) => {
            if (m.module_id === newModule.module_id) return

            const pkgModule = packageModules.find(
                pm => pm.module_id === m.module_id,
            )

            if (pkgModule && pkgModule.module.type === 'invoices') {
                indicesToRemove.push(index)
                if (pkgModule.add_on) {
                    pkgModule.checked = false
                    pkgModule.add_amount = 0
                    updateOptionalRefs(pkgModule)
                }
            }
        })

        removeByIndices(state.planProposal.proposal.modules, indicesToRemove)
        state.hasOptionalInvoice = true
        refreshDisplayFixedModules()
    }

    function restoreDefaultInvoiceModule(removedModule) {
        state.hasOptionalInvoice = false

        if (!state.planProposal.planPackage) return

        const defaultInvoice = state.planProposal.planPackage.modules.find(
            m =>
                m.module.type === 'invoices' &&
                !m.add_on &&
                m.module_id !== removedModule.module_id,
        )

        if (!defaultInvoice) return

        addOrReplaceModuleInProposal(state.planProposal, defaultInvoice)
        refreshDisplayFixedModules()
    }

    /**
     * Gerencia o check/uncheck de um módulo opcional
     */
    function checked(module) {
        const existingModuleIndex = findInProposal(module)

        if (module.checked) {
            module.add_amount = 1
            module.checked = true

            if (module.module.type === 'invoices') {
                replaceInvoiceModule(module)
            }

            addOrReplaceModuleInProposal(state.planProposal, module)

            if (hasCombo(module)) {
                showComboOptions(module)
            }
            if (hasDependency(module)) {
                checkDependency(module)
            }
            if (hasCourtesy(module)) {
                selectCourtesy(module)
            }
        } else {
            module.add_amount = 0
            module.checked = false

            restoreFixedOrRemoveFromProposal(module, existingModuleIndex)

            if (module.module.type === 'invoices') {
                restoreDefaultInvoiceModule(module)
            }

            if (hasCombo(module)) {
                hideComboOptions(module)
            }
            if (hasDependency(module)) {
                freeDependency(module)
            }
            if (hasCourtesy(module)) {
                removeCourtesy(module)
            }
        }

        updateTotalPrice()
        updateOptionalRefs(module)
    }

    /**
     * Gerencia a alteração de quantidade de um módulo
     */
    function addAmount(module) {
        const existingModuleIndex = findInProposal(module)

        if (module.add_amount > 0) {
            module.checked = true
            addOrReplaceModuleInProposal(state.planProposal, module)

            if (hasCourtesy(module)) {
                selectCourtesy(module)
            }
        } else if (existingModuleIndex !== -1) {
            restoreFixedOrRemoveFromProposal(module, existingModuleIndex)

            if (hasCourtesy(module)) {
                removeCourtesy(module)
            }
        }

        updateTotalPrice()
    }

    // ==================== COMBOS ====================

    /**
     * Mostra opções de combo para um módulo
     */
    function showComboOptions(module) {
        if (!state.planProposal || !state.planProposal.planPackage) return

        showParentComboOptions({
            parentModule: module,
            comboItems: state.comboItems,
            planPackageId: state.planProposal.planPackage.id,
            optionalModulesLength: getOptionalModules().length,
            createComboModules: moduleRelations.createComboModules.bind(
                moduleRelations,
            ),
        })
    }

    /**
     * Esconde opções de combo para um módulo
     */
    function hideComboOptions(module) {
        if (!state.planProposal || !state.planProposal.planPackage) return

        hideParentComboOptions({
            parentModule: module,
            comboItems: state.comboItems,
            proposalModules: state.planProposal.proposal.modules,
        })
    }

    // ==================== CORTESIAS ====================

    /**
     * Seleciona cortesias para um módulo
     */
    function selectCourtesy(module) {
        if (!state.planProposal) return

        const courtesyActions = moduleRelations.processCourtesyModules(
            module,
            state.planProposal.proposal.modules,
        )

        courtesyActions.forEach(({ action, index, data }) => {
            if (action === 'update') {
                Object.assign(state.planProposal.proposal.modules[index], data)
            } else if (action === 'add') {
                addOrReplaceModuleInProposal(state.planProposal, data)
            }
        })
    }

    /**
     * Remove cortesias de um módulo
     */
    function removeCourtesy(module) {
        const indicesToRemove = moduleRelations.removeCourtesyModules(
            module,
            state.planProposal.proposal.modules,
            getFixedModules(),
        )

        removeByIndices(state.planProposal.proposal.modules, indicesToRemove)

        // Se tem módulo fixo, reprocessar cortesia
        const fixedModule = getFixedModules().find(
            moduleFixed => moduleFixed.module_id === module.module_id,
        )
        if (fixedModule && indicesToRemove.length > 0) {
            selectCourtesy(module)
        }
    }

    // ==================== DEPENDÊNCIAS ====================

    /**
     * Verifica e ativa dependências de um módulo
     */
    function checkDependency(module) {
        if (!state.planProposal || !state.planProposal.planPackage) return

        const dependencies = moduleRelations.processDependencies(
            module,
            state.planProposal.planPackage.modules,
            getOptionalModules(),
        )

        dependencies.forEach(
            ({
                module: dependentModule,
                shouldCheck,
                shouldLock,
                addAmount,
            }) => {
                dependentModule.checked = shouldCheck
                dependentModule.add_amount = addAmount
                dependentModule.locked = shouldLock

                checked(dependentModule)
            },
        )
    }

    /**
     * Libera dependências de um módulo
     */
    function freeDependency(module) {
        const modulesToUnlock = moduleRelations.releaseDependencies(
            module,
            state.planProposal.planPackage.modules,
            getOptionalModules(),
            state.planProposal.proposal.modules,
        )

        if (!modulesToUnlock.length) return

        // Coletar índices para remover (em ordem reversa)
        const indicesToRemove = modulesToUnlock
            .map(({ indexInSelected }) => indexInSelected)
            .sort((a, b) => b - a)

        // Remover módulos da proposta
        indicesToRemove.forEach(index => {
            state.planProposal.proposal.modules.splice(index, 1)
        })

        // Atualizar estado dos módulos opcionais
        modulesToUnlock.forEach(({ module: dependentModule }) => {
            dependentModule.checked = false
            dependentModule.add_amount = 0
            dependentModule.locked = false
            updateOptionalRefs(dependentModule)
        })

        // Recalcular preço
        updateTotalPrice()
    }

    // ==================== CÁLCULOS DE PREÇO ====================

    /**
     * Atualiza o preço total da proposta
     */
    function updateTotalPrice() {
        const totalPrice = priceCalculator.calculateTotalPrice(
            state.planProposal.planPackage,
            state.planProposal.proposal.modules,
        )

        Vue.set(state.planProposal, 'total_price', totalPrice)
        Vue.set(state.planProposal.proposal, 'total_price', totalPrice)

        calculateDiscount()
        Vue.set(
            state.planProposal,
            'final_price',
            state.planProposal.final_price,
        )
    }

    /**
     * Calcula o desconto aplicado
     */
    function calculateDiscount() {
        if (discountCode && discountCode.value) {
            recalculateProposalPrices(state.planProposal, discountCode.value)
        } else {
            recalculateProposalPrices(state.planProposal, null)
        }
    }

    // ==================== HELPERS ====================

    /**
     * Atualiza referências do módulo no ModuleSelector
     */
    function updateOptionalRefs(module) {
        if (vue && vue.$refs && vue.$refs.moduleSelector) {
            vue.$refs.moduleSelector.updateModule(module)
        }
    }

    /**
     * Inicializa os módulos fixos do pacote
     */
    function initializeFixedModules() {
        getFixedModules().forEach(module => {
            if (!module.amount || module.amount === 0) {
                module.amount = 0
                module.add_amount = 1
            }
            addOrReplaceModuleInProposal(state.planProposal, module)

            if (module.courtesy && module.courtesy.length) {
                selectCourtesy(module)
            }
        })
    }

    /**
     * Seleciona um pacote de plano
     */
    function selectPlanPackage(planPackage, options = {}) {
        const { keepModules = false, customerPlan = null } = options

        // Limpar dados do pacote anterior
        state.comboItems = []

        if (!keepModules) {
            state.planProposal.proposal.modules = []
            state.hasOptionalInvoice = false
        }

        // Configurar novo pacote (Vue.set garante reatividade pois planPackage
        // não está em fields() do model e não é inicializado na instância)
        Vue.set(state.planProposal, 'planPackage', planPackage)

        // Se não houver customerPlan, reseta os valores do pacote
        // Se houver, usa os valores do plano atual do cliente
        if (!customerPlan) {
            state.planProposal.total_price = planPackage.price
            state.planProposal.final_price = planPackage.price
            state.planProposal.proposal.total_price = planPackage.price
            state.planProposal.proposal.final_price = planPackage.price
            state.planProposal.proposal.discount = 0
        } else {
            // Usa os valores do plano ativo do cliente
            state.planProposal.total_price = parseFloat(customerPlan.value)
            state.planProposal.final_price = parseFloat(
                customerPlan.final_price,
            )
            state.planProposal.proposal.total_price = parseFloat(
                customerPlan.value,
            )
            state.planProposal.proposal.final_price = parseFloat(
                customerPlan.final_price,
            )
            state.planProposal.proposal.discount = 0
        }

        // Inicializar módulos fixos
        if (!keepModules) {
            initializeFixedModules()
        }

        refreshDisplayFixedModules()

        // Recalcula preços apenas se não houver customerPlan
        if (!customerPlan) {
            updateTotalPrice()
        }
    }

    /**
     * Reseta o estado da proposta
     */
    function resetProposal() {
        state.planProposal = createInitialPlanProposal()
        state.comboItems = []
        state.hasOptionalInvoice = false
        state.displayFixedModules = []
    }

    return {
        planProposal: state.planProposal,
        comboItems: state.comboItems,

        // Computed (como getters para manter reatividade)
        get fixedModules() {
            return state.displayFixedModules
        },
        get optionalModules() {
            return getOptionalModules()
        },
        get addOnSelected() {
            return getAddOnSelected()
        },
        get showLoyaltyCondition() {
            return getShowLoyaltyCondition()
        },
        get askLoyalty() {
            return getAskLoyalty()
        },
        get summaryKey() {
            return getSummaryKey()
        },

        // Métodos de módulos
        addModule,
        checked,
        addAmount,

        // Métodos de combos
        showComboOptions,
        hideComboOptions,

        // Métodos de cortesias
        selectCourtesy,
        removeCourtesy,

        // Métodos de dependências
        checkDependency,
        freeDependency,

        // Métodos de preço
        updateTotalPrice,
        calculateDiscount,

        // Helpers
        updateOptionalRefs,
        initializeFixedModules,
        selectPlanPackage,
        resetProposal,
    }
}
